import { inject, observer } from "mobx-react";
import React, { Component } from "react";
import {
  BackgroundImage,
  DynamicLink,
  Overlay,
  P,
  Tag,
  LoadingRow,
  Row,
  Col,
} from "src/views/shared/components";
import { Icons } from "src/libs/assets";
import { tx } from "src/libs/i18n";
import { stripHtmlFromString, truncate } from "src/libs/utils";
import { CopyLink, DocumentInformation } from "src/views/shared/modals";
import { ISingleWordpressDocument } from "src/models";
import {
  AuthenticationStore,
  SearchStore,
  SectionsContainerStore,
  UIStore,
  LibraryStore,
  LoginStore,
} from "src/stores";
import * as s from "./DocumentRowItem.module.scss";
import { getFallbackImage } from "src/libs/utils/fallbacks";
import ConditionalWrapper from "resource-centre-theme/src/views/shared/components/ConditionalWrapper/ConditionalWrapper";
interface Props {
  searchStore?: SearchStore;
  sectionsContainerStore?: SectionsContainerStore;
  document: ISingleWordpressDocument;
  uiStore?: UIStore;
  authenticationStore?: AuthenticationStore;
  libraryStore?: LibraryStore;
  loginStore?: LoginStore;
  loading?: boolean;
}

interface State {
  authorsExpanded: boolean;
  linkOverlayOpen: boolean;
  informationOverlayOpen: boolean;
}

@inject(
  "searchStore",
  "sectionsContainerStore",
  "uiStore",
  "authenticationStore",
  "libraryStore",
  "loginStore"
)
@observer
export class DocumentRowItem extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      authorsExpanded: false,
      linkOverlayOpen: false,
      informationOverlayOpen: false,
    };
  }

  getAuthorNames = authors => {
    let authorNames = [];
    authors &&
      authors.forEach(author => {
        authorNames.push(author.name);
      });

    return authorNames;
  };

  renderAuthorsRow = authors => {
    const { authorsExpanded } = this.state;
    let authorsToRender = authors;
    let authorsNotRendered;
    let numberOfAuthorsNotRendered;
    let expandString;

    if (authors.length > 3 && !authorsExpanded) {
      authorsToRender = authors.slice(0, 3);
      authorsNotRendered = authors.slice(3);
      numberOfAuthorsNotRendered = authorsNotRendered.length;
      expandString = `+ ${numberOfAuthorsNotRendered} more`;
    }

    return (
      <div className={s.authorsContainer}>
        <P small className={s.authors}>
          {this.getAuthorNames(authorsToRender).join(", ")}
        </P>
        {authors.length > 3 && !authorsExpanded && (
          <P
            small
            className={s.expandAuthors}
            onClick={() => this.setState({ authorsExpanded: true })}
          >
            {expandString}
          </P>
        )}
      </div>
    );
  };

  renderAbstract = abstract => {
    const { document } = this.props;
    const { link } = document;
    const abstractLength = abstract.length;
    let abstractToLong = abstractLength > 180;

    return (
      <div className={s.abstract}>
        <span
          dangerouslySetInnerHTML={{ __html: truncate(abstract, 180) }}
        ></span>
        {abstractToLong && (
          <DynamicLink to={link} className={s.seeFullAbstract}>
            {tx("document.viewFullAbstract")}
          </DynamicLink>
        )}
      </div>
    );
  };

  toggleCopyLinkOverlay = () => {
    const { sectionsContainerStore } = this.props;
    const { linkOverlayOpen } = this.state;
    this.setState({ linkOverlayOpen: !linkOverlayOpen });

    if (!linkOverlayOpen) {
      sectionsContainerStore.setScrollPos();
    } else {
      sectionsContainerStore.resetScrollPos();
    }
  };

  toggleInformationOverlay = () => {
    const { sectionsContainerStore } = this.props;
    const { informationOverlayOpen } = this.state;
    this.setState({ informationOverlayOpen: !informationOverlayOpen });

    if (!informationOverlayOpen) {
      sectionsContainerStore.setScrollPos();
    } else {
      sectionsContainerStore.resetScrollPos();
    }
  };

  renderLoadingLayout = () => {
    return (
      <>
        <div className={s.documentRow}>
          <div className={s.loadingContainer}>
            <Row>
              <Col xs={3} className={s.loadingImageSkeleton}>
                <LoadingRow width={190} height={250} fixed />
              </Col>

              <Col xs={9}>
                <LoadingRow
                  width={150}
                  style={{ display: "block", marginBottom: "10px" }}
                />
                <LoadingRow
                  width={400}
                  height={30}
                  style={{ display: "block", marginBottom: "10px" }}
                />
                <LoadingRow
                  width={150}
                  style={{ display: "block", marginBottom: "30px" }}
                />
                <LoadingRow
                  width={500}
                  height={80}
                  style={{ display: "block", marginBottom: "10px" }}
                />
              </Col>
            </Row>
          </div>
        </div>
      </>
    );
  };

  render() {
    const { linkOverlayOpen, informationOverlayOpen } = this.state;
    const {
      document,
      uiStore,
      authenticationStore,
      libraryStore,
      searchStore,
      loginStore,
    } = this.props;
    const { isSearching } = searchStore;
    const { userHasValidToken } = authenticationStore;
    const { documentInUserCollection, onOpenModal } = libraryStore;
    const { openLoginModal } = loginStore;
    const {
      title,
      link,
      type,
      documentFields,
      documentAuthors,
      documentPublishers,
      documentThumbnail,
      documentContentTypes,
    } = document || {};
    const { authors } = documentAuthors || {};
    const { publicationYear, documentFlares, abstract, documents } =
      documentFields || {};
    const { publishers } = documentPublishers || {};
    const { contentTypes } = documentContentTypes || {};
    const { recommended, peerReviewed } = documentFlares || {};
    const { isDesktop } = uiStore;
    const contentType = contentTypes?.length
      ? contentTypes[0]
      : { name: type, link: "" };
    const image =
      documentThumbnail?.image ?? document.image ?? getFallbackImage(documents);
    if (isSearching) return this.renderLoadingLayout();
    return (
      <>
        {!isDesktop && contentType ? (
          <DynamicLink to={contentType?.link}>
            <P small className={s.contentType}>
              {contentType?.name}
            </P>
          </DynamicLink>
        ) : (
          <></>
        )}
        <div className={s.documentRow}>
          {linkOverlayOpen && (
            <Overlay>
              <CopyLink
                toggleOverlay={this.toggleCopyLinkOverlay}
                link={link}
              />
            </Overlay>
          )}

          {informationOverlayOpen && (
            <Overlay>
              <DocumentInformation
                toggleOverlay={this.toggleInformationOverlay}
                document={document}
              />
            </Overlay>
          )}
          <div className={s.ctaRow}>
            <div className={s.ctaIcon} data-hover={tx("icons.link")}>
              <img
                src={Icons.link_small}
                alt={tx("icons.link")}
                onClick={this.toggleCopyLinkOverlay}
              />
            </div>

            <div
              className={s.ctaIcon}
              data-hover={
                !documentInUserCollection(document.databaseId)
                  ? tx("icons.addToLibrary")
                  : tx("icons.addedToLibrary")
              }
            >
              <img
                src={
                  !documentInUserCollection(document.databaseId)
                    ? Icons.library_small
                    : Icons.library_small_grey
                }
                alt={tx("icons.addToLibrary")}
                onClick={
                  userHasValidToken
                    ? () => {
                        document.nodeType = "Document";
                        onOpenModal(document);
                      }
                    : openLoginModal
                }
              />
            </div>

            <div className={s.ctaIcon} data-hover={tx("icons.viewAndDownload")}>
              <img
                src={Icons.more_small}
                alt={tx("icons.viewAndDownload")}
                onClick={this.toggleInformationOverlay}
              />
            </div>
          </div>
          <ConditionalWrapper
            condition={isDesktop}
            wrapper={children => (
              <DynamicLink to={link} className={s.image}>
                {children}
              </DynamicLink>
            )}
          >
            <div className={s.overlay} />
            <BackgroundImage
              image={image || getFallbackImage(documents)}
              objectFit="cover"
              className={s.backgroundImage}
            />
          </ConditionalWrapper>
          <div className={s.informationContainer}>
            {isDesktop && contentType ? (
              <DynamicLink to={contentType?.link}>
                <P small className={s.contentType}>
                  {contentType?.name}
                </P>
              </DynamicLink>
            ) : (
              <></>
            )}
            <DynamicLink to={link}>
              <h6 className={s.title}>{title}</h6>
            </DynamicLink>
            <div className={s.tags}>
              {peerReviewed && (
                <Tag size="small" title={"Peer reviewed"} rounded />
              )}
              {recommended && (
                <Tag size="small" title={"Recommended"} rounded />
              )}
            </div>
            <div className={s.publishingInformation}>
              <P className={s.publishingInformationLabel}>
                {publicationYear}
                {publicationYear && publishers?.[0] && " · "}
                {publishers?.[0]?.name}
              </P>
            </div>
            {authors && this.renderAuthorsRow(authors)}
            {abstract && this.renderAbstract(stripHtmlFromString(abstract))}
          </div>
        </div>
      </>
    );
  }
}
